import {
	CollectionResponseDTO,
	GalleryResponseDTO,
	PhotoResponseDTO,
	PSApiOperations,
	UploadRequestDTO,
} from "@/api/PSApiOperations"
import { atom } from "jotai"
import { atomWithQuery } from "jotai-tanstack-query"
import { AxiosError } from "axios"
import { store } from "../store"
import { PhotoRecord } from "@/lib/catalog"
import {
	beginPublishSession,
	finishPreparedPublishSession,
	startPublishPhotos,
	type PublishCollectionSnapshot,
} from "../editor/photo-publishing"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import {
	ExportImageOptions,
	publishOptionsAtom,
	publishSelectedOptionAtom,
	type SelectedOption,
} from "@/core/export-image"
import { getPublishFilenames } from "./publish-filenames"
import {
	allPhotosAtom,
	allHiddenPhotosAtom,
	exportablePhotosAtom,
	selectedExportablePhotosAtom,
} from "../photo/photos-state"
import { updateCurrentProject } from "../project/projects-state"
import { galleryPhotosAtom, PixiesetService } from "./PixiesetService"
import { queryClient } from "@/api/constants"
import {
	collectionIdAtom,
	galleryIdAtom,
	workspaceIdAtom,
	isPhotoStorageAvailableAtom,
} from "./pixieset-state"
import { toNumOrNull } from "@/utils/number"
import { isBrowserOffline, isNetworkConnectivityError } from "@/api/network-behavior"
import { clearPixiesetCollectionSelection } from "./pixieset-selection"
import { isAbortError } from "@/utils/error"
import { replaceDialog } from "../dialogs-state"
import { isAuthReadyAtom } from "../auth-state"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"
import { getExportResizeOptions } from "@/core/export-resize-options"
import { getPublishExportSettingsKey } from "./publish-export-settings"

export const selectedCollectionQueryAtom = atomWithQuery<CollectionResponseDTO | null>((get) => {
	const collectionId = get(collectionIdAtom)
	const normalizedCollectionId = collectionId ? String(collectionId) : null
	const workspaceId = get(workspaceIdAtom)

	return {
		queryKey: ["collection", workspaceId, normalizedCollectionId],
		enabled: get(isAuthReadyAtom) && workspaceId !== null && normalizedCollectionId !== null,
		queryFn: async () => {
			if (!normalizedCollectionId) {
				return null
			}
			try {
				return await PixiesetService.getCollection(normalizedCollectionId)
			} catch (error) {
				if (
					error instanceof AxiosError &&
					(error.response?.status === 403 || error.response?.status === 404)
				) {
					return null
				}
				throw error
			}
		},
	}
})

export const galleriesQueryAtom = atomWithQuery<GalleryResponseDTO[]>((get) => {
	const collectionId = get(collectionIdAtom)
	const normalizedCollectionId = collectionId ? String(collectionId) : null
	const workspaceId = get(workspaceIdAtom)

	return {
		queryKey: ["galleries", workspaceId, normalizedCollectionId],
		enabled: get(isAuthReadyAtom) && workspaceId !== null && normalizedCollectionId !== null,
		queryFn: () =>
			normalizedCollectionId ? PixiesetService.listGalleries(normalizedCollectionId) : [],
	}
})

export const resolveGalleryId = (
	galleries: GalleryResponseDTO[],
	currentGalleryId: string | null | undefined,
) => {
	if (currentGalleryId && galleries.some((gallery) => gallery.id === currentGalleryId)) {
		return currentGalleryId
	}
	return galleries[0]?.id ?? null
}

export const collectionAtom = atom(
	(get) => get(selectedCollectionQueryAtom).data ?? null,
	(get, _set, collection: CollectionResponseDTO | null) => {
		const currentCollectionId = get(collectionIdAtom)
		if (!collection) {
			if (currentCollectionId) {
				clearPixiesetCollectionSelection()
			}
			return
		}
		if (currentCollectionId?.toString() === collection.id.toString()) {
			return
		}
		const workspaceId = get(workspaceIdAtom)
		if (workspaceId) {
			queryClient.setQueryData(["collection", workspaceId, String(collection.id)], collection)
		}
		void updateCurrentProject({ publishCollectionId: collection.id })
		store.set(galleryIdAtom, null)
		store.set(isPhotoStorageAvailableAtom, undefined)
	},
)

export const getGalleryUpdates = (props: {
	galleryId: string
	exportablePhotos: PhotoRecord[]
	selectedPhotos: PhotoRecord[]
	selectedOption: SelectedOption
	publishFilenames: Record<string, string>
	hiddenPhotos: PhotoRecord[]
	galleryPhotos: PhotoResponseDTO[]
	publishExportSettingsKey: string
}) => {
	const {
		galleryId,
		exportablePhotos,
		selectedPhotos,
		selectedOption,
		publishFilenames,
		hiddenPhotos,
		galleryPhotos,
		publishExportSettingsKey,
	} = props
	const selectedPhotoIds = new Set(selectedPhotos.map((photo) => photo.id))
	// exportablePhotos comes from sortedPhotosAtom and is the current editor display order.
	// Preserve that order here because selectedPhotos follows selection insertion order instead.
	const sourcePhotos =
		selectedOption === "all"
			? exportablePhotos
			: exportablePhotos.filter((photo) => selectedPhotoIds.has(photo.id))
	if (galleryPhotos.length === 0) {
		return {
			photosToUpload: sourcePhotos,
			photosToUpdate: [],
			photosToDelete: [],
			photosToPublish: sourcePhotos,
			publishFilenames,
			hasChanges: sourcePhotos.length > 0,
			isLoading: false,
		}
	}

	const photosToUpload: PhotoRecord[] = []
	const photosToUpdate: PhotoRecord[] = []
	const photosToDelete: PhotoResponseDTO[] = []
	const photosToPublish: PhotoRecord[] = []

	sourcePhotos.forEach((photo) => {
		const exportedFilename = publishFilenames[photo.id]
		if (!exportedFilename) return

		const existingPhoto = galleryPhotos.find(
			(galleryPhoto) => galleryPhoto.name === exportedFilename,
		)
		if (existingPhoto) {
			const publishedDate = photo.publishedDates?.[galleryId]
			const publishedExportSettingsKey = photo.publishedExportSettings?.[galleryId]
			if (!publishedDate) {
				photosToUpdate.push(photo)
				photosToPublish.push(photo)
			} else if (photo.adjustedDate && publishedDate < photo.adjustedDate) {
				photosToUpdate.push(photo)
				photosToPublish.push(photo)
			} else if (publishedExportSettingsKey !== publishExportSettingsKey) {
				photosToUpdate.push(photo)
				photosToPublish.push(photo)
			}
		} else {
			photosToUpload.push(photo)
			photosToPublish.push(photo)
		}
	})

	if (selectedOption === "all") {
		const exportableFilenames = new Set(
			exportablePhotos.flatMap((photo) => {
				const filename = publishFilenames[photo.id]
				return filename ? [filename] : []
			}),
		)
		galleryPhotos.forEach((galleryPhoto) => {
			const existsInExportablePhotos = exportableFilenames.has(galleryPhoto.name)
			if (!existsInExportablePhotos) {
				const canDelete = hiddenPhotos.some((photo) => {
					const exportedName = publishFilenames[photo.id]
					const publishedDate = photo.publishedDates?.[galleryId]
					// If the photo was previously published by PN, we can delete it since it's now hidden
					return exportedName === galleryPhoto.name && publishedDate !== undefined
				})
				if (canDelete) {
					photosToDelete.push(galleryPhoto)
				}
			}
		})
	}

	return {
		photosToUpload,
		photosToUpdate,
		photosToDelete,
		photosToPublish,
		publishFilenames,
		hasChanges:
			photosToUpload.length > 0 || photosToUpdate.length > 0 || photosToDelete.length > 0,
		isLoading: false,
	}
}

export const publishFilenamesAtom = atom((get) => {
	// Compute from the whole project so a photo keeps the same Pixieset filename
	// whether it is published alone, with a selection, or with all photos.
	const projectPhotos = get(allPhotosAtom)
	return getPublishFilenames(projectPhotos)
})

export const galleryUpdatesAtom = atom((get) => {
	const galleryId = get(galleryIdAtom)
	if (!galleryId) {
		return {
			photosToUpload: [],
			photosToUpdate: [],
			photosToDelete: [],
			photosToPublish: [],
			publishFilenames: {},
			hasChanges: false,
			isLoading: false,
		}
	}

	const exportablePhotos = get(exportablePhotosAtom)
	const selectedPhotos = get(selectedExportablePhotosAtom)
	const selectedOption = get(publishSelectedOptionAtom)
	const publishOptions = get(publishOptionsAtom)
	const publishExportSettingsKey = getPublishExportSettingsKey({
		quality: publishOptions.quality,
		colorSpace: publishOptions.colorSpace,
		ppi: publishOptions.pixelPerInch,
		...getExportResizeOptions(
			publishOptions.dimensionType,
			publishOptions.dimensionSide,
			publishOptions.dimension,
		),
	})
	const publishFilenames = get(publishFilenamesAtom)
	const hiddenPhotos = get(allHiddenPhotosAtom)
	const { data: galleryPhotos, isLoading } = get(galleryPhotosAtom)
	if (isLoading) {
		return {
			photosToUpload: [],
			photosToUpdate: [],
			photosToDelete: [],
			photosToPublish: [],
			publishFilenames: {},
			hasChanges: false,
			isLoading: true,
		}
	}
	return getGalleryUpdates({
		galleryId,
		exportablePhotos,
		selectedPhotos,
		selectedOption,
		publishFilenames,
		hiddenPhotos,
		galleryPhotos,
		publishExportSettingsKey,
	})
})

export const setUpWorkspace = async () => {
	const workspaceId = store.get(workspaceIdAtom)
	if (workspaceId === null) {
		await PSApiOperations.getWorkspaces().then((response) => {
			const workspace = response?.data?.[0]
			if (workspace) {
				store.set(workspaceIdAtom, workspace.id_hash)
			}
		})
	}
}

export const checkForStorageAvailability = () => {
	const workspaceId = store.get(workspaceIdAtom)
	if (!workspaceId) {
		return
	}

	const collectionId = store.get(collectionIdAtom)
	if (!collectionId) {
		return
	}

	const galleryId = store.get(galleryIdAtom)
	if (!galleryId) {
		//We can't check storage availability without a gallery, will check again when gallery is set
		return
	}
	//Use before_upload endpoint to check storage availability, ideally PS should provide a dedicated endpoint for this
	const uploadRequest: UploadRequestDTO = {
		files: [
			{
				name: "test.jpg", // just a placeholder for checking storage
				type: "image/jpeg",
				size: 1, // Size does not matter to BE
			},
		],
	}

	void PSApiOperations.requestUpload({
		galleryId,
		requestBody: uploadRequest,
	})
		.then((uploadRequestResponse) => {
			store.set(
				isPhotoStorageAvailableAtom,
				uploadRequestResponse.photos_storage_status === "available",
			)
		})
		.catch((error) => {
			console.error("Error checking photo storage availability:", error)
		})
}

const showPreUploadPublishNetworkDialog = () => {
	replaceDialog(ConfirmDialog, {
		title: i18n.t("store:storeClientGallery.publishFailedTitle"),
		message: i18n.t("store:storeClientGallery.publishFailedMessage"),
		primary: {
			title: i18n.t("common:action.ok"),
		},
		canClose: true,
		trackingViewName: "publish_network_connectivity_failed",
	})
}

export const publishPhotos = async (
	photos: PhotoRecord[],
	exportOptions: ExportImageOptions,
	photosToDelete: PhotoResponseDTO[],
	publishFilenames: Record<string, string> = getPublishFilenames(photos),
): Promise<boolean> => {
	const currentCollection = store.get(collectionAtom)
	const publishCollection: PublishCollectionSnapshot = {
		id: currentCollection?.id ?? null,
		name: currentCollection?.name ?? null,
	}
	const currentWatermarkId = toNumOrNull(currentCollection?.["watermark_id"])

	// Get the selected gallery.
	const galleryId = store.get(galleryIdAtom)
	if (!galleryId) {
		throw new Error("No gallery found for the collection")
	}
	const photosWithFilenames = photos.flatMap((photo) => {
		const filename = publishFilenames[photo.id]
		return filename ? [{ photo, filename }] : []
	})
	const photosToPublish = photosWithFilenames.map(({ photo }) => photo)
	const session = await beginPublishSession(photosToPublish.length)
	if (!session) {
		return false
	}

	//Request upload
	const uploadRequest: UploadRequestDTO = {
		files: photosWithFilenames.map(({ filename }) => ({
			name: filename,
			type: "image/jpeg",
			size: 1, // Size does not matter to BE
		})),
	}

	let didHandleOfflineNetworkError = false
	try {
		const uploadRequestResponse =
			photosWithFilenames.length > 0
				? await PSApiOperations.requestUpload({
						galleryId,
						requestBody: uploadRequest,
					}).catch((error) => {
						if (session.isCanceled) {
							return undefined
						}

						if (isNetworkConnectivityError(error) && isBrowserOffline()) {
							didHandleOfflineNetworkError = true
							showPreUploadPublishNetworkDialog()
							return undefined
						}

						throw error
					})
				: undefined

		if (didHandleOfflineNetworkError) {
			await finishPreparedPublishSession(session, { dismissDialog: false })
			return true
		}

		session.throwIfCanceled()

		if (!uploadRequestResponse && photosWithFilenames.length > 0) {
			throw new Error("Failed to request upload")
		}
		//Do a final check for storage availability before publishing
		if (uploadRequestResponse && uploadRequestResponse.photos_storage_status !== "available") {
			store.set(isPhotoStorageAvailableAtom, false)
			throw new Error("Photo storage not available.")
		}

		// process watermark
		if (publishCollection.id) {
			const watermarkId = toNumOrNull(exportOptions.watermark)
			if (watermarkId !== currentWatermarkId) {
				await PSApiOperations.setWatermark({
					collectionId: Number(publishCollection.id),
					watermarkId: watermarkId,
				})
			}
		}

		session.throwIfCanceled()

		void startPublishPhotos(
			photosToPublish,
			galleryId,
			uploadRequestResponse,
			exportOptions,
			photosToDelete,
			publishCollection,
			publishFilenames,
			session,
		).catch((error) => {
			if (isAbortError(error)) {
				console.warn("[Publish] publish flow canceled", error)
				return
			}

			console.error("Error publishing photos:", error)
		})
		return true
	} catch (error) {
		if (session.isCanceled || isAbortError(error)) {
			console.warn("[Publish] publish preparation canceled", error)
			await finishPreparedPublishSession(session, { canceled: true })
			return true
		}

		await finishPreparedPublishSession(session)
		throw error
	}
}

const hashStringToUint32 = (value: string): number => {
	let hash = 2166136261
	for (let i = 0; i < value.length; i += 1) {
		hash ^= value.charCodeAt(i)
		hash = Math.imul(hash, 16777619)
	}
	return hash >>> 0
}

// Mulberry32 PRNG: https://stackoverflow.com/a/47593316
// Uses specific constants for state stepping and mixing to produce
// a fast, decent-quality 32-bit pseudorandom stream.
const createSeededRandom = (seed: string | number): (() => number) => {
	let state = hashStringToUint32(String(seed)) || 1
	return () => {
		state += 0x6d2b79f5
		let t = Math.imul(state ^ (state >>> 15), 1 | state)
		t ^= t + Math.imul(t ^ (t >>> 7), 61 | t)
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296
	}
}

export const getRandomReferencePhotosFromCollection = async (
	collectionId: string,
	count: number,
	seed?: string | number,
): Promise<PhotoResponseDTO[]> => {
	const allPhotos = await getPhotosFromCollection(collectionId)
	if (!allPhotos || allPhotos.length === 0) return []

	const seedValue = seed ?? collectionId
	if (count >= allPhotos.length) {
		return allPhotos
	}
	const random = createSeededRandom(seedValue)
	const shuffled = allPhotos
		.map((photo) => ({ sort: random(), value: photo }))
		.sort((a, b) => a.sort - b.sort)
		.map((entry) => entry.value)
	return shuffled.slice(0, count)
}

export const getPhotosFromCollection = async (collectionId: string) => {
	return PixiesetService.getDefaultGalleryId(collectionId).then((galleryId) => {
		if (!galleryId) {
			throw new Error("No gallery found for the collection")
		}
		return PixiesetService.listPhotos(galleryId).then((photos) => {
			if (!photos) {
				throw new Error("No photos found in the gallery")
			}

			return photos
		})
	})
}
